import { sidePanelOpened } from 'features/side-panels/sidePanelsSlice';
import { takeEvery, takeLatest } from 'redux-saga/effects';
import {
  chatImageUploadFailed,
  clearPublicChatRequested,
  deleteChatImage,
  deleteChatMessage,
  downloadChatImageRequested,
  signalingAllChatMessagesReceived,
  signalingChatImageDeleted,
  signalingChatMessageSent,
  signalingChatReactionToggled,
  signalingPublicChatCleared,
  toggleChatReaction,
} from 'features/chat/actions';
import { signalingRoomJoined } from 'features/room/actions';
import { sendMessageSaga } from 'features/chat/sagas/sendMessageSaga';
import { deleteMessageSaga } from 'features/chat/sagas/deleteMessageSaga';
import { deleteImageSaga } from 'features/chat/sagas/deleteImageSaga';
import { onChatImageDeletedSaga } from 'features/chat/sagas/onChatImageDeletedSaga';
import { onChatMessageSentSaga } from 'features/chat/sagas/onChatMessageSentSaga';
import {
  activeTabChanged,
  chatMessagesReset,
  exportChatRequested,
  messageSent,
  privateChatOpened,
  returnedToPrivateChatsView,
  sendChatMessage,
} from 'features/chat/chatSlice';
import { onPrivateChatOpenedSaga } from 'features/chat/sagas/onPrivateChatOpenedSaga';
import { exportChatSaga } from 'features/chat/sagas/exportChatSaga';
import { clearPublicChatSaga } from 'features/chat/sagas/clearPublicChatSaga';
import { clearPendingImagesSaga } from 'features/chat/sagas/clearPendingImagesOnChatChangeSaga';
import { downloadChatImageSaga } from 'features/chat/sagas/downloadChatImageSaga';
import { onChatImageUploadFailedSaga } from 'features/chat/sagas/onChatImageUploadFailedSaga';
import { cancelAllChatUploadsSaga } from 'features/chat/sagas/cancelAllChatUploadsSaga';
import { updateMessageCounterSaga } from './updateMessageCounterSaga';
import { onActiveTabChangedSaga } from './onActiveTabChangedSaga';
import { onChatToolbarPanelOpenedSaga } from './onChatToolbarPanelOpenedSaga';
import { onPublicChatClearedSaga } from './onPublicChatClearedSaga';
import { toggleChatReactionSaga } from './toggleChatReactionSaga';
import { onChatReactionToggledSaga } from './onChatReactionToggledSaga';

export default function* chatWatcher() {
  // takeEvery, not takeLatest: an image send keeps running (attached upload
  // fork) after the saga's synchronous part finishes — takeLatest would cancel
  // that in-flight upload as soon as the next message is sent, stranding the
  // optimistic message with no failure path.
  yield takeEvery(sendChatMessage, sendMessageSaga);
  yield takeEvery(messageSent, updateMessageCounterSaga);
  yield takeEvery(signalingChatMessageSent, onChatMessageSentSaga);

  yield takeLatest(deleteChatMessage, deleteMessageSaga);
  yield takeEvery(deleteChatImage, deleteImageSaga);
  yield takeEvery(signalingChatImageDeleted, onChatImageDeletedSaga);

  yield takeEvery(privateChatOpened, onPrivateChatOpenedSaga);

  yield takeEvery(activeTabChanged, onActiveTabChangedSaga);
  yield takeEvery(sidePanelOpened, onChatToolbarPanelOpenedSaga);

  yield takeEvery(activeTabChanged, clearPendingImagesSaga);
  yield takeEvery(privateChatOpened, clearPendingImagesSaga);
  yield takeEvery(returnedToPrivateChatsView, clearPendingImagesSaga);

  yield takeLatest(exportChatRequested, exportChatSaga);
  yield takeLatest(clearPublicChatRequested, clearPublicChatSaga);
  yield takeLatest(signalingPublicChatCleared, onPublicChatClearedSaga);

  yield takeEvery(toggleChatReaction, toggleChatReactionSaga);
  yield takeEvery(signalingChatReactionToggled, onChatReactionToggledSaga);

  yield takeEvery(downloadChatImageRequested, downloadChatImageSaga);

  yield takeEvery(chatImageUploadFailed, onChatImageUploadFailedSaga);
  // Every action whose reducer resets the message history must also run the
  // upload cleanup, otherwise in-flight uploads and preview blob URLs leak.
  yield takeEvery(chatMessagesReset, cancelAllChatUploadsSaga);
  yield takeEvery(signalingRoomJoined, cancelAllChatUploadsSaga);
  yield takeEvery(signalingAllChatMessagesReceived, cancelAllChatUploadsSaga);
}
